import { mergeWith, pick } from 'lodash';

import { type ShortUrlResponse } from '@air/api/types';

import { replacePresentationSubObjectsMergeCustomizer } from '~/swr-hooks/shortUrl/replacePresentationSubObjectsMergeCustomizer';
import { type ShareUrlUpdateHandlerProps } from '~/swr-hooks/shortUrl/useUpdateShortId';

/** Fields allowed on `ShortUrl.update` for forms — identity/list metadata is excluded. */
const UPDATABLE_FORM_SHORT_URL_FIELDS = [
  'passcode',
  'disabled',
  'requireAccount',
  'expirationDate',
  'permissions',
  'presentationSettings',
  'presentationContent',
  'presentationPages',
  'boardContext',
] as const;

type FormShareLinkUpdateBaseline = Pick<ShortUrlResponse, (typeof UPDATABLE_FORM_SHORT_URL_FIELDS)[number]>;

const getFormShareLinkApiBaseline = (shortIdInfo: ShortUrlResponse): FormShareLinkUpdateBaseline => {
  const baseline = pick(shortIdInfo, UPDATABLE_FORM_SHORT_URL_FIELDS);
  const presentationSettings = baseline.presentationSettings;

  if (!presentationSettings) {
    return baseline;
  }

  // Workspace defaults persist `customLogo` onto new form links; the update API rejects
  // that field for objectType form, so it must not ride along on an unrelated PATCH.
  const { customLogo: _customLogo, ...presentationSettingsWithoutCustomLogo } = presentationSettings;

  return {
    ...baseline,
    presentationSettings: presentationSettingsWithoutCustomLogo,
  };
};

export const mergeFormShareLinkApiUpdatePatch = (
  shortIdInfo: ShortUrlResponse,
  patch: ShareUrlUpdateHandlerProps,
): FormShareLinkUpdateBaseline => {
  const merged = pick(
    mergeWith({}, getFormShareLinkApiBaseline(shortIdInfo), patch, replacePresentationSubObjectsMergeCustomizer),
    UPDATABLE_FORM_SHORT_URL_FIELDS,
  );

  if (patch.presentationSettings !== undefined) {
    return merged;
  }

  // `presentationSettings` is a shallow PATCH — omit the stored object entirely when this save
  // did not touch it, so leftover branding keys cannot be re-validated.
  const { presentationSettings: _presentationSettings, ...withoutPresentationSettings } = merged;

  return withoutPresentationSettings;
};
